import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueries, useQuery } from 'react-query'
import HeaderTitleContent from '../../../common/components/atom/HeaderTitleContent/HeaderTitleContent'
import Table from '../../../common/components/molecule/Table/Table'
import TagFilter from '../../../common/components/molecule/TagFilter/TagFilter'
import TagCellEditor from '../../../common/components/molecule/TagCellEditor/TagCellEditor'
import useLuminaireFleetMetrics from '../../../features/luminaire/hooks/useLuminaireFleetMetrics'
import useUpdateLuminaireTags from '../../../features/luminaire/hooks/useUpdateLuminaireTags'
import { PermissionTooltip, useCapability } from '../../../features/rbac'
import { useKnownLuminaireTags } from '../../../features/luminaire/hooks/useLuminaires'
import LuminaireFleetMetrics from './LuminaireFleetMetrics'
import EnergyKwhMini from '../../../common/components/atom/EnergyKwhMini'
import DimmingMini from '../../../common/components/atom/DimmingMini'
import { StatusCardMiniView } from '../../../common/components/atom/StatusCard/StatusCardMini'
import { planBTAPI } from '../../../features/planBT/services/planAPI'
import streetLightAPI from '../../../features/planBT/services/streetLightAPI'
import minigridAPI from '../../../features/minigrid/service/minigridAPI'
import { useAuth } from '../../../features/auth/hooks'
import ColumnSettings, {
  ColumnDefinition,
  ColumnSettingsToolbar,
  loadColumnPreference,
  saveColumnPreference,
} from '../../../common/components/molecule/ColumnSettings/ColumnSettings'

type NumericSort = 'NONE' | 'ASC' | 'DESC'
type OnlineFilter = 'ALL' | 'ON' | 'OFF'

type LuminaireColumnKey =
  | 'name'
  | 'devEui'
  | 'minigrid'
  | 'pole'
  | 'phase'
  | 'status'
  | 'energy'
  | 'dimming'
  | 'online'
  | 'tags'

const ALL_LUMINAIRE_COLUMNS: ReadonlyArray<ColumnDefinition<LuminaireColumnKey>> = [
  { key: 'name', label: 'Nom' },
  { key: 'devEui', label: 'devEUI' },
  { key: 'minigrid', label: 'Minigrid' },
  { key: 'pole', label: 'Poteau' },
  { key: 'phase', label: 'Phase' },
  { key: 'status', label: 'État' },
  { key: 'energy', label: 'Énergie active' },
  { key: 'dimming', label: 'Dimming' },
  { key: 'online', label: 'Statut' },
  { key: 'tags', label: 'Tags' },
]

const ALL_LUMINAIRE_KEYS: ReadonlyArray<LuminaireColumnKey> = ALL_LUMINAIRE_COLUMNS.map((c) => c.key)
const STORAGE_KEY = 'luminaireList.visibleColumns'

const formatLastSeen = (iso: string | null): string | null => {
  if (!iso) return null
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return null
  return t.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const filterInputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 180,
  height: 26,
  padding: '2px 6px',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  background: '#fff',
  fontSize: 12,
  color: '#1f2937',
  fontWeight: 400,
}

export default function LuminaireListPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const minigridParam = searchParams.get('minigrid') ?? ''
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [nameQuery, setNameQuery] = useState('')
  const [devEuiQuery, setDevEuiQuery] = useState('')
  const [poleQuery, setPoleQuery] = useState('')
  const [phaseFilter, setPhaseFilter] = useState('ALL')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>('ALL')
  const [energySort, setEnergySort] = useState<NumericSort>('NONE')
  const [dimmingSort, setDimmingSort] = useState<NumericSort>('NONE')
  const [energyMin, setEnergyMin] = useState('')
  const [energyMax, setEnergyMax] = useState('')
  const [dimmingMin, setDimmingMin] = useState('')
  const [dimmingMax, setDimmingMax] = useState('')
  const { luminaires, byDevEui, isLoading } = useLuminaireFleetMetrics({ tags: selectedTags })
  const updateTagsMutation = useUpdateLuminaireTags()
  const canEditTags = useCapability('equipment.edit')
  const knownTags = useKnownLuminaireTags()
  const { getAccountIdUser } = useAuth()
  const accountId = getAccountIdUser()

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<LuminaireColumnKey[]>(() =>
    loadColumnPreference<LuminaireColumnKey>(STORAGE_KEY, ALL_LUMINAIRE_KEYS),
  )

  useEffect(() => {
    saveColumnPreference(STORAGE_KEY, visibleColumns)
  }, [visibleColumns])

  const visibleSet = useMemo(() => new Set(visibleColumns), [visibleColumns])
  const orderedVisibleColumns = useMemo(
    () => ALL_LUMINAIRE_COLUMNS.filter((c) => visibleSet.has(c.key)),
    [visibleSet],
  )

  const allPlansQuery = useQuery({
    queryKey: ['allPlanBTForLuminaireList'],
    queryFn: () => planBTAPI.getAllPlanBT(0, 0),
    staleTime: 60 * 1000,
  })
  const plans = useMemo(() => allPlansQuery.data?.data ?? [], [allPlansQuery.data])

  const streetLightQueries = useQueries(
    plans.map((p) => ({
      queryKey: ['streetLightsByPlan', p.plan_id] as const,
      queryFn: () => streetLightAPI.getAllStreetLights(p.plan_id),
      staleTime: 60 * 1000,
      enabled: Boolean(p.plan_id),
    })),
  )

  const allMinigridsQuery = useQuery({
    queryKey: ['allMinigridsByAccount', accountId],
    queryFn: () => minigridAPI.getAllMinigrids(0, 0, { accountId }),
    enabled: Boolean(accountId),
    staleTime: 5 * 60 * 1000,
  })
  const minigridNameById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const mg of allMinigridsQuery.data?.data ?? []) {
      map[mg.minigrid_id] = mg.minigrid_name
    }
    return map
  }, [allMinigridsQuery.data])

  const slSignature = streetLightQueries.map((q) => q.dataUpdatedAt).join(',')
  const minigridNameByDevEui = useMemo(() => {
    const map: Record<string, string> = {}
    plans.forEach((plan, idx) => {
      const minigridName = plan.plan_minigrid
        ? minigridNameById[plan.plan_minigrid]
        : undefined
      if (!minigridName) return
      const sls = streetLightQueries[idx]?.data?.data ?? []
      for (const sl of sls) {
        const devEui = sl.str_light_zhaga_devui?.trim()
        if (devEui) map[devEui] = minigridName
      }
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, minigridNameById, slSignature])

  const minigridSuggestions = useMemo(() => {
    const set = new Set<string>()
    for (const name of Object.values(minigridNameByDevEui)) {
      if (name) set.add(name)
    }
    return Array.from(set).sort()
  }, [minigridNameByDevEui])

  const [minigridFilter, setMinigridFilter] = useState<string>(minigridParam)

  useEffect(() => {
    setMinigridFilter(minigridParam)
  }, [minigridParam])

  const poleSuggestions = useMemo(() => {
    const set = new Set<string>()
    for (const l of luminaires) {
      if (l.lum_pole_number && l.lum_pole_number.trim() !== '') set.add(l.lum_pole_number.trim())
    }
    return Array.from(set).sort()
  }, [luminaires])

  const phaseOptions = useMemo(() => {
    const set = new Set<string>()
    for (const l of luminaires) {
      if (l.lum_phase_number && l.lum_phase_number.trim() !== '') set.add(l.lum_phase_number.trim())
    }
    return Array.from(set).sort()
  }, [luminaires])

  const statusOptions = useMemo(() => {
    const set = new Set<string>()
    for (const l of luminaires) {
      if (l.lum_status && l.lum_status.trim() !== '') set.add(l.lum_status.trim())
    }
    return Array.from(set).sort()
  }, [luminaires])

  const eMinVal = energyMin.trim() === '' ? null : Number(energyMin)
  const eMaxVal = energyMax.trim() === '' ? null : Number(energyMax)
  const eMinActive = eMinVal !== null && !Number.isNaN(eMinVal)
  const eMaxActive = eMaxVal !== null && !Number.isNaN(eMaxVal)
  const dMinVal = dimmingMin.trim() === '' ? null : Number(dimmingMin)
  const dMaxVal = dimmingMax.trim() === '' ? null : Number(dimmingMax)
  const dMinActive = dMinVal !== null && !Number.isNaN(dMinVal)
  const dMaxActive = dMaxVal !== null && !Number.isNaN(dMaxVal)

  const filteredLuminaires = useMemo(() => {
    const n = nameQuery.trim().toLowerCase()
    const d = devEuiQuery.trim().toLowerCase()
    const p = poleQuery.trim().toLowerCase()
    return luminaires.filter((l) => {
      if (n !== '' && !(l.lum_name || '').toLowerCase().includes(n)) return false
      if (d !== '' && !(l.lum_dev_eui || '').toLowerCase().includes(d)) return false
      if (p !== '' && !(l.lum_pole_number || '').toLowerCase().includes(p)) return false
      if (phaseFilter !== 'ALL' && l.lum_phase_number !== phaseFilter) return false
      if (statusFilter !== 'ALL' && l.lum_status !== statusFilter) return false
      if (minigridFilter !== '' && minigridNameByDevEui[l.lum_dev_eui] !== minigridFilter) return false
      if (onlineFilter !== 'ALL') {
        // ON ne matche que les flags ON ; OFF couvre OFF + NA (pas de signal du tout
        // ⇒ traité comme hors ligne, équivalent au pattern compteurs).
        const flag = byDevEui[l.lum_dev_eui]?.onlineFlag ?? 'NA'
        if (onlineFilter === 'ON' && flag !== 'ON') return false
        if (onlineFilter === 'OFF' && flag === 'ON') return false
      }
      if (eMinActive || eMaxActive) {
        const v = byDevEui[l.lum_dev_eui]?.activeEnergyKwh ?? null
        if (v === null) return false
        if (eMinActive && v < (eMinVal as number)) return false
        if (eMaxActive && v > (eMaxVal as number)) return false
      }
      if (dMinActive || dMaxActive) {
        const v = byDevEui[l.lum_dev_eui]?.dimmingLevelPercent ?? null
        if (v === null) return false
        if (dMinActive && v < (dMinVal as number)) return false
        if (dMaxActive && v > (dMaxVal as number)) return false
      }
      return true
    })
  }, [luminaires, nameQuery, devEuiQuery, poleQuery, phaseFilter, statusFilter, onlineFilter, byDevEui, eMinActive, eMaxActive, eMinVal, eMaxVal, dMinActive, dMaxActive, dMinVal, dMaxVal, minigridFilter, minigridNameByDevEui])

  const sortedLuminaires = useMemo(() => {
    const getVal = (dev: string, which: 'energy' | 'dimming'): number | null => {
      const t = byDevEui[dev]
      if (!t) return null
      return which === 'energy' ? t.activeEnergyKwh ?? null : t.dimmingLevelPercent ?? null
    }
    const cmp = (a: number | null, b: number | null, dir: 1 | -1) => {
      if (a === null && b === null) return 0
      if (a === null) return 1
      if (b === null) return -1
      return (a - b) * dir
    }
    if (energySort !== 'NONE') {
      const dir = energySort === 'ASC' ? 1 : -1
      return [...filteredLuminaires].sort((a, b) =>
        cmp(getVal(a.lum_dev_eui, 'energy'), getVal(b.lum_dev_eui, 'energy'), dir),
      )
    }
    if (dimmingSort !== 'NONE') {
      const dir = dimmingSort === 'ASC' ? 1 : -1
      return [...filteredLuminaires].sort((a, b) =>
        cmp(getVal(a.lum_dev_eui, 'dimming'), getVal(b.lum_dev_eui, 'dimming'), dir),
      )
    }
    return filteredLuminaires
  }, [filteredLuminaires, byDevEui, energySort, dimmingSort])

  const bodyData = sortedLuminaires.map((lum) => {
    const telemetry = byDevEui[lum.lum_dev_eui]
    const minigridName = minigridNameByDevEui[lum.lum_dev_eui] ?? '-'
    const onlineFlag = telemetry?.onlineFlag ?? 'NA'
    const lastSeenLabel = formatLastSeen(telemetry?.lastSeen ?? null)

    const cellsByKey: Record<LuminaireColumnKey, string | number | React.ReactNode> = {
      name: lum.lum_name,
      devEui: lum.lum_dev_eui,
      minigrid: minigridName,
      pole: lum.lum_pole_number,
      phase: lum.lum_phase_number,
      status: lum.lum_status,
      energy: (
        <EnergyKwhMini
          key={`energy-${lum.lum_id}`}
          value={telemetry?.activeEnergyKwh ?? null}
        />
      ),
      dimming: (
        <DimmingMini
          key={`dimming-${lum.lum_id}`}
          value={telemetry?.dimmingLevelPercent ?? null}
        />
      ),
      online: (
        <StatusCardMiniView
          key={`online-${lum.lum_id}`}
          isOnline={onlineFlag === 'ON'}
          deviceId={lum.lum_dev_eui}
          formattedDate={lastSeenLabel}
        />
      ),
      tags: (
        <PermissionTooltip allowed={canEditTags} key={`tags-${lum.lum_id}`}>
          <TagCellEditor
            tags={lum.lum_tags ?? []}
            onSave={(next) => updateTagsMutation.mutateAsync({ luminaire: lum, tags: next })}
            disabled={!canEditTags}
            suggestions={knownTags}
          />
        </PermissionTooltip>
      ),
    }

    return {
      id: lum.lum_dev_eui,
      data: orderedVisibleColumns.map((col) => cellsByKey[col.key]),
    }
  })

  const poleListId = 'luminaire-pole-suggestions'
  const headerFiltersByKey: Record<LuminaireColumnKey, React.ReactNode | null> = {
    name: <input key="name" type="text" value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} placeholder="Nom…" style={filterInputStyle} onClick={(e) => e.stopPropagation()} />,
    devEui: <input key="dev" type="text" value={devEuiQuery} onChange={(e) => setDevEuiQuery(e.target.value)} placeholder="devEUI…" style={filterInputStyle} onClick={(e) => e.stopPropagation()} />,
    minigrid: (
      <select key="minigrid" value={minigridFilter} onChange={(e) => setMinigridFilter(e.target.value)} style={filterInputStyle} onClick={(e) => e.stopPropagation()}>
        <option value="">Tous</option>
        {minigridSuggestions.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
    ),
    pole: (
      <div key="pole">
        <input
          type="text"
          list={poleListId}
          value={poleQuery}
          onChange={(e) => setPoleQuery(e.target.value)}
          placeholder="Localisation…"
          style={filterInputStyle}
          onClick={(e) => e.stopPropagation()}
        />
        <datalist id={poleListId}>
          {poleSuggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>
    ),
    phase: (
      <select key="phase" value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} style={filterInputStyle} onClick={(e) => e.stopPropagation()}>
        <option value="ALL">Toutes</option>
        {phaseOptions.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
    ),
    status: (
      <select key="status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={filterInputStyle} onClick={(e) => e.stopPropagation()}>
        <option value="ALL">Tous</option>
        {statusOptions.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    ),
    energy: (
      <div key="energy" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <select value={energySort} onChange={(e) => { setEnergySort(e.target.value as NumericSort); if (e.target.value !== 'NONE') setDimmingSort('NONE') }} style={filterInputStyle}>
          <option value="NONE">Tri</option>
          <option value="ASC">Croissant</option>
          <option value="DESC">Décroissant</option>
        </select>
        <div style={{ display: 'flex', gap: 4 }}>
          <input type="number" value={energyMin} onChange={(e) => setEnergyMin(e.target.value)} placeholder="Min" style={filterInputStyle} />
          <input type="number" value={energyMax} onChange={(e) => setEnergyMax(e.target.value)} placeholder="Max" style={filterInputStyle} />
        </div>
      </div>
    ),
    dimming: (
      <div key="dimming" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <select value={dimmingSort} onChange={(e) => { setDimmingSort(e.target.value as NumericSort); if (e.target.value !== 'NONE') setEnergySort('NONE') }} style={filterInputStyle}>
          <option value="NONE">Tri</option>
          <option value="ASC">Croissant</option>
          <option value="DESC">Décroissant</option>
        </select>
        <div style={{ display: 'flex', gap: 4 }}>
          <input type="number" value={dimmingMin} onChange={(e) => setDimmingMin(e.target.value)} placeholder="Min" style={filterInputStyle} />
          <input type="number" value={dimmingMax} onChange={(e) => setDimmingMax(e.target.value)} placeholder="Max" style={filterInputStyle} />
        </div>
      </div>
    ),
    online: (
      <select key="online" value={onlineFilter} onChange={(e) => setOnlineFilter(e.target.value as OnlineFilter)} style={filterInputStyle} onClick={(e) => e.stopPropagation()}>
        <option value="ALL">Tous</option>
        <option value="ON">En ligne</option>
        <option value="OFF">Hors ligne</option>
      </select>
    ),
    tags: null,
  }

  const headerTitle = orderedVisibleColumns.map((c) => c.label)
  const headerFilters = orderedVisibleColumns.map((c) => headerFiltersByKey[c.key])

  return (
    <div>
      <HeaderTitleContent>Luminaires</HeaderTitleContent>
      <LuminaireFleetMetrics tags={selectedTags} />
      <TagFilter value={selectedTags} onChange={setSelectedTags} suggestions={knownTags} />
      <ColumnSettingsToolbar onClick={() => setSettingsOpen(true)} />
      <Table
        headerTitle={headerTitle}
        headerFilters={headerFilters}
        bodyData={bodyData}
        onRowClick={(row) => navigate('/main/luminaires/' + row.id)}
        isLoading={isLoading}
        emptyMessage="Aucun luminaire trouvé"
      />
      <ColumnSettings
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        columns={ALL_LUMINAIRE_COLUMNS}
        visibleKeys={visibleColumns}
        onChange={setVisibleColumns}
      />
    </div>
  )
}
